import { useLingui } from '@lingui/react';
// PresenceDot — the agent runtime indicator.
//
// Blueprint agent-lifecycle.md §2.3: a dot never stands alone. Every dot
// renders a sibling label (visible, or sr-only + title in compact mode), so a
// grey circle can never leave the reader guessing.
//
// The DOM contract other surfaces and the e2e suite read:
//   data-presence   'online' | 'offline' | 'error' | 'disabled'
//   data-task-state 'busy' | 'idle', empty otherwise — busy/idle are still
//                   connected, so they keep data-presence='online' and carry
//                   the task dimension in this second attribute.
//   data-reason     the error cause code, empty otherwise.
//
// Only agent rows get a dot; the caller decides, this component does not
// inspect roles.
import { describeAgentState, DISABLED_LABEL } from '../lib/agent-state';
import type { AgentRuntimeReason, AgentRuntimeState } from '../lib/api';

export interface PresenceDotProps {
  state: AgentRuntimeState | undefined;
  reason: AgentRuntimeReason | undefined;
  /** The owner switched this agent off. Outranks `state`, which the server pins to 'offline'. */
  disabled?: boolean;
  /** Compact mode: dot only, label moves into title + sr-only (dense lists such as the sidebar). */
  compact?: boolean;
}

// Unknown / absent states fall back to offline rather than rendering nothing,
// so the row still says what it is.
function normalizeState(s: AgentRuntimeState | undefined): 'online' | 'offline' | 'error' {
  if (s === 'online' || s === 'error') return s;
  if (s === 'busy' || s === 'idle') return 'online';
  return 'offline';
}

function taskStateAttr(s: AgentRuntimeState | undefined): '' | 'busy' | 'idle' {
  if (s === 'busy' || s === 'idle') return s;
  return '';
}

export default function PresenceDot({
  state,
  reason,
  disabled = false,
  compact = false,
}: PresenceDotProps) {
  const { i18n } = useLingui();
  const normalized = disabled ? 'disabled' : normalizeState(state);
  const taskState = disabled ? '' : taskStateAttr(state);
  const label = disabled ? i18n._(DISABLED_LABEL) : describeAgentState(state, reason, i18n).text;
  // Dot colours live entirely in CSS so a restyle cannot silently degrade the
  // accessible contrast of a state.
  const dotClass = taskState
    ? `presence-dot presence-task-${taskState}`
    : `presence-dot presence-${normalized}`;

  if (compact) {
    return (
      <span
        className="presence-inline presence-inline-compact"
        data-presence={normalized}
        data-task-state={taskState}
        data-reason={disabled ? '' : (reason ?? '')}
        data-failure-badge={normalized === 'error' ? 'true' : ''}
        title={label}
      >
        <span className={dotClass} aria-hidden="true" />
        <span className="sr-only">{label}</span>
      </span>
    );
  }
  return (
    <span
      className="presence-inline"
      data-presence={normalized}
      data-task-state={taskState}
      data-reason={disabled ? '' : (reason ?? '')}
      data-failure-badge={normalized === 'error' ? 'true' : ''}
    >
      <span className={dotClass} aria-hidden="true" />
      <span className="presence-text">{label}</span>
    </span>
  );
}
